// src/AdminDashboard.js

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { logout } from '../services/authService';
import api from '../services/api';
import './AdminDashboard.css';

const base = process.env.PUBLIC_URL || '';

const IconEvent = () => (
    <img src={`${base}/images/icon-event.png`} alt="" aria-hidden />
);
const IconBCP = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);
const IconQR = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="4" height="4" rx="0.5" />
        <rect x="9" y="14" width="4" height="4" rx="0.5" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <path d="M14 17h2v2h-2zM18 17h2v2h-2z" fill="currentColor" />
    </svg>
);

const IconCardSetting = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18" />
        <path d="M7 15h4" />
    </svg>
);

// 説明PDF管理（各機能画面の使い方PDF）
const IconHelp = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
        <path d="M6 3h9l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
        <path d="M14 3v5h5" />
        <path d="M9.5 12.5a2 2 0 1 1 2.8 1.8c-.5.3-.8.7-.8 1.2" />
        <circle cx="11.5" cy="18" r="0.6" fill="currentColor" stroke="none" />
    </svg>
);

const AdminDashboard = () => {
    const [pendingApplicationCount, setPendingApplicationCount] = useState(0);

   
    const adminUserRaw = localStorage.getItem("adminUser");
    const user = adminUserRaw ? JSON.parse(adminUserRaw) : null;
    const isStaff = user?.is_staff === true || user?.is_superuser === true;
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];

 
    const hasEventPerm = isStaff || perms.includes("CAN_MANAGE_CREATE") || perms.includes("CAN_MANAGE_EVENT");
    const hasAppPerm = isStaff || perms.includes("CAN_MANAGE_APPLICATION"); 
    const hasSalaryPerm = isStaff || perms.includes("CAN_MANAGE_SALARY");
    const hasNoticePerm = isStaff || perms.includes("CAN_MANAGE_NOTICE");
    const hasWorktimePerm = isStaff || perms.includes("CAN_MANAGE_WORKTIME");
    const hasEmergencyPerm = isStaff || perms.includes("CAN_MANAGE_EMERGENCY");
    const hasLeavePerm = isStaff || perms.includes("CAN_MANAGE_LEAVE");
    const hasQrPerm = isStaff || perms.includes("CAN_MANAGE_QR");
    const hasSettingPerm = isStaff || perms.includes("CAN_MANAGE_SETTING");
    const hasQuotePerm = isStaff || perms.includes("CAN_MANAGE_QUOTE");

    /** 数字を丸数字に変換（0〜10）。それ以外はそのまま */
    const numberToCircle = (num) => {
        const n = parseInt(num, 10);
        if (Number.isNaN(n)) return num;
        const CIRCLE_NUMBER_BASE = 9311;
        if (n === 0) return '⓪';
        if (n >= 1 && n <= 10) return String.fromCodePoint(CIRCLE_NUMBER_BASE + n);
        return String(n);
    };

    useEffect(() => {
      
        if (hasAppPerm) {
            api.get('/api/admin/applications/', { params: { _t: Date.now() } })
                .then((res) => {
                    const list = Array.isArray(res.data) ? res.data : [];
                    const count = list.filter((a) => a.status === 'SUBMITTED').length;
                    setPendingApplicationCount(count);
                })
                .catch(() => setPendingApplicationCount(0));
        }
    }, [hasAppPerm]);

    const handleLogout = () => {
        logout();
        window.location.href = '/admin/login';
    };

    const renderIconItem = (to, className, icon, text, hasAccess) => {
        if (!hasAccess) {
          
            return (
                <div className="admin-icon-item disabled-menu-item" style={{ opacity: 0.25, cursor: 'not-allowed' }}>
                    <span className={`admin-icon-box ${className}`} style={{ filter: 'grayscale(100%)' }}>
                        {icon}
                    </span>
                    <span className="admin-icon-text">{text}</span>
                </div>
            );
        }
      
        return (
            <Link to={to} className="admin-icon-item">
                <span className={`admin-icon-box ${className}`}>
                    {icon}
                </span>
                <span className="admin-icon-text">{text}</span>
            </Link>
        );
    };

    return (
        <div className="container mt-4">
            <div className='topbar'>
                <h2 className="topbar-title">社員管理</h2>
                <button onClick={() => { if (window.confirm('ログアウトしますか？')) handleLogout(); }}
                    className="logout-btn" aria-label="ログアウト">
                    ログアウト
                </button>
            </div>
            
            <div className="admin-icon-grid">
                {/* 1. イベント */}
                {renderIconItem("/admin/create_event", "event", <IconEvent />, "イベント", hasEventPerm)}

                {/* 2. 申請 */}
                {renderIconItem("/admin/applications", "applications", (
                    <div className="icon-box-label">
                        <img src={`${base}/images/icon-form.png`} alt="" aria-hidden />
                        {pendingApplicationCount > 0 && (
                            <span className="admin-applications-badge" aria-label={`申請中${pendingApplicationCount}件`}>
                                {pendingApplicationCount <= 10
                                    ? numberToCircle(pendingApplicationCount)
                                    : `${pendingApplicationCount}`}
                            </span>
                        )}
                    </div>
                ), "申請", hasAppPerm)}

                {/* 3. 給与 */}
                {renderIconItem("/admin/salary", "salary", <img src={`${base}/images/icon-pay.png`} alt="" aria-hidden />, "給与", hasSalaryPerm)}

                {/* 4. お知らせ */}
                {renderIconItem("/admin/notice", "notice", <img src={`${base}/images/icon-notice.png`} alt="" aria-hidden />, "お知らせ", hasNoticePerm)}

                {/* 5. 出退勤確認 */}
                {renderIconItem("/admin/workstime", "worktime", <img src={`${base}/images/icon-time.png`} alt="" aria-hidden />, "出退勤確認", hasWorktimePerm)}

                {/* 6. 緊急連絡網 */}
                {renderIconItem("/admin/emergency_contact", "emergency", <img src={`${base}/images/icon-phone.png`} alt="" aria-hidden />, "緊急連絡網", hasEmergencyPerm)}

                {/* 7. 有給休暇管理 */}
                {renderIconItem("/admin/leave", "leave", <img src={`${base}/images/icon-leave.jpg`} alt="" aria-hidden />, "有給休暇管理", hasLeavePerm)}

                {/* 8. QR生成 */}
                {renderIconItem("/admin/qr", "qr", <IconQR />, "QR生成", hasQrPerm)}
                
                {/* 9. 社員証・会社設定 */}
                {renderIconItem("/admin/employee-card-setting", "card-setting", <IconCardSetting />, "社員証・会社設定", hasSettingPerm)}

                {/* 10. 今日の一言管理 */}
                {renderIconItem("/admin/quotes", "quotes", <IconHelp />, "今日の一言管理", hasQuotePerm)} 

         
            </div>
        </div>
    );
};

export default AdminDashboard;